import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Plus, Loader2, Shield, UserPlus, Power, X, Mail } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
};

export default function AdminAdmins() {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [includeInactive, setIncludeInactive] = useState(false);
    const [showCreate, setShowCreate] = useState(false);

    const load = () => {
        setLoading(true);
        apiPrivate.get(`/admin/admins?include_inactive=${includeInactive}`)
            .then(r => setAdmins(r.data || []))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [includeInactive]);

    const action = async (id, op, confirmMsg, successMsg) => {
        if (confirmMsg && !window.confirm(confirmMsg)) return;
        try {
            await apiPrivate.post(`/admin/admins/${id}/${op}`);
            toast.success(successMsg);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore operazione");
        }
    };

    const activeCount = admins.filter(a => a.is_active).length;

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-admins">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <span className="label-tag text-magenta">Amministratori</span>
                        <h1 className="font-display text-5xl mt-3 tracking-tighter">
                            Chi può <span className="font-serif-italic text-gradient-tropical">gestire</span> la piattaforma.
                        </h1>
                        <p className="text-zinc-400 mt-3 max-w-2xl">
                            Aggiungi nuovi admin via email e disattiva quelli dismessi. Deve sempre restare almeno un admin attivo — l'invariante è applicata server-side.
                        </p>
                    </div>
                    <button onClick={() => setShowCreate(true)} className="btn-rebel" data-testid="new-admin-btn">
                        <Plus className="h-4 w-4" /> Nuovo admin
                    </button>
                </div>

                {/* KPI */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
                    <div className="bento p-5" data-testid="kpi-active">
                        <div className="text-xs text-zinc-500 uppercase tracking-widest">Admin attivi</div>
                        <div className="text-4xl font-display tracking-tighter mt-2 text-bayside">{activeCount}</div>
                    </div>
                    <div className="bento p-5" data-testid="kpi-inactive">
                        <div className="text-xs text-zinc-500 uppercase tracking-widest">Disattivati</div>
                        <div className="text-4xl font-display tracking-tighter mt-2 text-zinc-500">{admins.length - activeCount}</div>
                    </div>
                    <div className="bento p-5">
                        <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">Mostra disattivati</div>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} className="accent-magenta h-4 w-4" data-testid="toggle-inactive" />
                            <span className="text-sm">{includeInactive ? "Sì" : "No"}</span>
                        </label>
                    </div>
                </div>

                {/* Table */}
                <div className="bento p-6 mt-8">
                    {loading ? (
                        <div className="p-8 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                    ) : admins.length === 0 ? (
                        <div className="p-12 text-center text-zinc-500" data-testid="admins-empty">
                            <Shield className="h-10 w-10 mx-auto mb-3 opacity-40" />
                            Nessun admin trovato.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs text-zinc-500 uppercase tracking-widest">
                                    <tr className="border-b border-zinc-800">
                                        <th className="text-left py-3">Admin</th>
                                        <th className="text-left py-3">Creato</th>
                                        <th className="text-left py-3">Stato</th>
                                        <th className="text-right py-3">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {admins.map(a => (
                                        <tr key={a.id} className="border-b border-zinc-900/60 hover:bg-zinc-900/30" data-testid={`admin-row-${a.id}`}>
                                            <td className="py-3">
                                                <div className="flex items-center gap-2">
                                                    <div className={`h-9 w-9 rounded-full flex items-center justify-center ${a.is_active ? "bg-magenta/15 text-magenta" : "bg-zinc-800 text-zinc-500"}`}>
                                                        <Shield className="h-4 w-4" />
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold flex items-center gap-2">
                                                            {[a.nome, a.cognome].filter(Boolean).join(" ") || a.email}
                                                            {a.is_self && <span className="px-2 py-0.5 rounded-full bg-bayside/15 text-bayside text-xs">tu</span>}
                                                        </div>
                                                        <div className="text-xs text-zinc-500">{a.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-3 text-xs text-zinc-500">{fmtDate(a.created_at)}</td>
                                            <td className="py-3">
                                                <span className={`px-2 py-0.5 rounded-full text-xs ${a.is_active ? "bg-bayside/15 text-bayside" : "bg-zinc-700/40 text-zinc-400"}`}>
                                                    {a.is_active ? "Attivo" : "Disattivato"}
                                                </span>
                                            </td>
                                            <td className="py-3 text-right">
                                                {a.is_self ? (
                                                    <span className="text-xs text-zinc-600 italic">— azioni bloccate —</span>
                                                ) : a.is_active ? (
                                                    <button onClick={() => action(a.id, "deactivate", "Disattivare l'admin? Non potrà più accedere.", "Admin disattivato")} className="btn-ghost text-xs text-vermillion" data-testid={`deactivate-${a.id}`}>
                                                        <Power className="h-3.5 w-3.5" /> Disattiva
                                                    </button>
                                                ) : (
                                                    <button onClick={() => action(a.id, "reactivate", null, "Admin riattivato")} className="btn-ghost text-xs text-bayside" data-testid={`reactivate-${a.id}`}>
                                                        <Power className="h-3.5 w-3.5" /> Riattiva
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {showCreate && <CreateAdminModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
            </div>
        </AppLayout>
    );
}

function CreateAdminModal({ onClose, onSaved }) {
    const [form, setForm] = useState({ email: "", nome: "", cognome: "" });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!form.email || !form.nome) {
            toast.error("Email e nome sono obbligatori");
            return;
        }
        setSaving(true);
        try {
            await apiPrivate.post("/admin/admins", form);
            toast.success("Admin creato — email di benvenuto inviata");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="create-admin-modal">
            <div className="bento p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
                <div className="flex items-start gap-3 mb-4">
                    <div className="h-10 w-10 rounded-full bg-magenta/15 text-magenta flex items-center justify-center">
                        <UserPlus className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-xl font-display tracking-tighter">Nuovo amministratore</h3>
                        <p className="text-xs text-zinc-500 mt-1">Riceverà un'email di benvenuto con istruzioni di accesso OTP.</p>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs text-zinc-500 uppercase tracking-widest">Email *</label>
                        <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="nuovo.admin@startupitalia.eu" className="input-field" data-testid="form-email" autoFocus />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-zinc-500 uppercase tracking-widest">Nome *</label>
                            <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} className="input-field" data-testid="form-nome" />
                        </div>
                        <div>
                            <label className="text-xs text-zinc-500 uppercase tracking-widest">Cognome</label>
                            <input value={form.cognome} onChange={e => setForm({ ...form, cognome: e.target.value })} className="input-field" data-testid="form-cognome" />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="btn-ghost">Annulla</button>
                    <button onClick={submit} disabled={saving} className="btn-rebel" data-testid="form-submit">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                        Crea e invita
                    </button>
                </div>
            </div>
        </div>
    );
}
